import type { ApiError } from '@/types/common'
import type { HttpClient } from './types'

/**
 * Talks to the Laravel API (`/api/v1/...`). Not used while VITE_USE_MOCKS is on —
 * kept ready so each domain service switches by changing one export, not a rewrite.
 *
 * In dev, Vite proxies /api to the backend; in Docker, nginx does. Set
 * VITE_API_URL only when the API lives on another origin.
 */
const API_BASE_URL = `${import.meta.env.VITE_API_URL ?? ''}/api/v1`

/**
 * Two independent sessions can exist side by side in one browser: a tenant user
 * and an EReach staff member. They are stored under separate keys and the right
 * one is chosen by the path, because the API refuses a token from the wrong side.
 */
export type TokenKind = 'tenant' | 'platform'

const TOKEN_KEYS: Record<TokenKind, string> = {
  tenant: 'ereach.auth.token',
  platform: 'ereach.platformAuth.token',
}

export function setAuthToken(token: string | null, remember = true, kind: TokenKind = 'tenant'): void {
  const key = TOKEN_KEYS[kind]
  // Always clear both first: signing in without "remember me" after a remembered
  // session would otherwise leave the old token behind, and getAuthToken reads
  // localStorage first — so the stale one would win.
  localStorage.removeItem(key)
  sessionStorage.removeItem(key)
  if (token === null) return
  ;(remember ? localStorage : sessionStorage).setItem(key, token)
}

export function getAuthToken(kind: TokenKind = 'tenant'): string | null {
  const key = TOKEN_KEYS[kind]
  return localStorage.getItem(key) ?? sessionStorage.getItem(key)
}

/** Everything under /platform is EReach Admin; everything else is the tenant app. */
function tokenKindFor(path: string): TokenKind {
  return path.startsWith('/platform') ? 'platform' : 'tenant'
}

/**
 * Called when the API rejects our token (expired, revoked, or the account is
 * gone). Set once at startup; keeps the router and stores out of this module.
 */
let onUnauthorized: ((kind: TokenKind) => void) | null = null

export function setUnauthorizedHandler(handler: (kind: TokenKind) => void): void {
  onUnauthorized = handler
}

/** Laravel's 422 body ({ message, errors: { field: [msg] } }) mapped onto our ApiError shape. */
function toApiError(status: number, body: Record<string, unknown> | null): ApiError {
  if (status === 422 && body && typeof body === 'object' && 'errors' in body) {
    const errors = body.errors as Record<string, string[]>
    const fieldErrors = Object.fromEntries(
      Object.entries(errors).map(([field, messages]) => [field, messages[0] ?? '']),
    )
    return {
      code: 'validation_failed',
      message: (body.message as string) ?? 'Verifică datele completate.',
      fieldErrors,
    }
  }

  const codeByStatus: Record<number, string> = {
    401: 'unauthorized',
    403: 'forbidden',
    404: 'not_found',
    410: 'token_expired',
    429: 'rate_limited',
  }

  return {
    code: codeByStatus[status] ?? `http_${status}`,
    message: (body?.message as string) ?? 'A apărut o eroare neașteptată. Încearcă din nou.',
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const kind = tokenKindFor(path)
  const token = getAuthToken(kind)
  let response: Response
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...init.headers,
      },
    })
  } catch {
    // fetch only rejects on network-level failures — anything else is an HTTP status.
    throw {
      code: 'network_error',
      message: 'Conexiunea a eșuat. Verifică internetul și încearcă din nou.',
    } satisfies ApiError
  }

  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as Record<string, unknown> | null
    if (response.status === 401) {
      // A dead token can't be retried into working: drop that session so the
      // user lands on login instead of staring at permanent skeletons.
      setAuthToken(null, true, kind)
      onUnauthorized?.(kind)
    }
    throw toApiError(response.status, body)
  }

  if (response.status === 204) return undefined as T
  return (await response.json()) as T
}

/**
 * Object params are sent as `filters[city]=X` rather than JSON, because that's
 * what Laravel's request()->input('filters') expects to receive.
 */
function withQuery(url: string, params?: Record<string, unknown>): string {
  if (!params) return url
  const query = new URLSearchParams()

  const append = (key: string, value: unknown): void => {
    if (value === undefined || value === null || value === '') return
    if (Array.isArray(value)) {
      value.forEach((item) => append(`${key}[]`, item))
      return
    }
    if (typeof value === 'object') {
      Object.entries(value as Record<string, unknown>).forEach(([k, v]) => append(`${key}[${k}]`, v))
      return
    }
    query.append(key, String(value))
  }

  Object.entries(params).forEach(([key, value]) => append(key, value))
  const qs = query.toString()
  return qs ? `${url}?${qs}` : url
}

export const httpClient: HttpClient = {
  get: (url, params) => request(withQuery(url, params)),
  post: (url, body) => request(url, { method: 'POST', body: JSON.stringify(body) }),
  put: (url, body) => request(url, { method: 'PUT', body: JSON.stringify(body) }),
  patch: (url, body) => request(url, { method: 'PATCH', body: JSON.stringify(body) }),
  delete: (url) => request(url, { method: 'DELETE' }),
}
